/**
 * MainLayout - 인증된 페이지들의 공통 레이아웃
 * - 상단 헤더 (DashboardHeader)
 * - 하위 콘텐츠
 * - 하단 법정 고지 푸터 (LegalFooter)
 */
import React, { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { DashboardHeader } from '@/components/common/dashboard/DashboardHeader';
import { PasswordChangeDialog } from '@/components/common/dashboard/PasswordChangeDialog';
import { SettingsModal } from '@/components/common/dashboard/SettingsModal';
import { LegalFooter } from './LegalFooter';

const REDUCED_MOTION_KEY = 'reduced_motion';

export const MainLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const savedReducedMotion = localStorage.getItem(REDUCED_MOTION_KEY) === 'true';
    setReducedMotion(savedReducedMotion);
    if (savedReducedMotion) {
      document.body.classList.add('reduced-motion');
    }
  }, []);

  const handleReducedMotionChange = (checked: boolean) => {
    setReducedMotion(checked);
    localStorage.setItem(REDUCED_MOTION_KEY, String(checked));
    if (checked) {
      document.body.classList.add('reduced-motion');
    } else {
      document.body.classList.remove('reduced-motion');
    }
  };

  const handleLogout = () => {
    logout();
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* 상단 헤더 - 항상 표시 */}
      <DashboardHeader
        user={user}
        onPasswordChange={() => setIsPasswordDialogOpen(true)}
        onLogout={handleLogout}
        onSettingsClick={() => setShowSettings(true)}
      />

      {/* 페이지 콘텐츠 */}
      <Outlet />

      {/* 법정 고지 (결제가 있는 서비스라 모든 화면에 둔다) */}
      <LegalFooter />

      {/* 비밀번호 변경 다이얼로그 */}
      <PasswordChangeDialog
        open={isPasswordDialogOpen}
        onOpenChange={setIsPasswordDialogOpen}
      />

      {/* 설정 모달 */}
      <SettingsModal
        open={showSettings}
        onClose={() => setShowSettings(false)}
        reducedMotion={reducedMotion}
        onReducedMotionChange={handleReducedMotionChange}
      />
    </div>
  );
};

export default MainLayout;
